<template>
    <v-row dense class="mb-4">
        <v-col cols="12" md="2">
            <span class="text-h6 text--secondary">
                {{ $t('key_facts') }}
            </span>
        </v-col>
        <v-col cols="12" md="8">
            <v-row dense>
                <v-col cols="12" sm="6">
                    <div class="text-subtitle-1 font-weight-medium">{{ $t('countries_covered_by_insurance') }}*</div>
                </v-col>
                <v-col cols="12" sm="6">
                    <div class="text-subtitle-1 font-weight-medium">{{ $t('available_for_citizens') }}*</div>
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12" sm="6">
                    <v-radio-group v-model="contract.allLocations" density="compact">
                        <v-radio
                            :label="$t('all_countries')"
                            :value="true"
                            color="primary"
                            data-cy="all-locations"
                            @click="contract.insuranceLocations = []"
                        />
                        <v-radio
                            :label="$t('select_from_list')"
                            :value="false"
                            color="primary"
                            data-cy="select-from-list-locations"
                        />
                    </v-radio-group>
                    <CountryTable v-if="!contract.allLocations" v-model="contract.insuranceLocations" />
                </v-col>
                <v-col cols="12" sm="6">
                    <v-radio-group v-model="contract.allCitizenships" density="compact">
                        <v-radio
                            :label="$t('all_countries')"
                            :value="true"
                            color="primary"
                            data-cy="all-citizenships"
                            @click="contract.citizenships = []"
                        />
                        <v-radio
                            :label="$t('select_from_list')"
                            :value="false"
                            color="primary"
                            data-cy="select-from-list-citizenships"
                        />
                    </v-radio-group>
                    <CountryTable v-if="!contract.allCitizenships" v-model="contract.citizenships" />
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12">
                    <div class="text-subtitle-">{{ $t('insurance_risks') }}:</div>
                    <v-checkbox
                        v-model="contract.accident"
                        hide-details
                        :label="$t('travel_accident_insurance')"
                        density="compact"
                        class="pb-1"
                        data-cy="travel-accident-insurance"
                    />
                    <v-checkbox
                        v-model="contract.notLeave"
                        hide-details
                        :label="$t('trip_cancellation_coverage')"
                        density="compact"
                        class="pb-4"
                        data-cy="trip-cancellation-coverage"
                    />
                </v-col>
            </v-row>
        </v-col>
    </v-row>
    <v-row dense class="mb-4">
        <v-col cols="12" md="2">
            <span class="text-h6 text--secondary">
                {{ $t('documents') }}
            </span>
            <div class="px-0 font-italic text-subtitle-2">{{ $t('insurance_document_note') }}</div>
        </v-col>
        <v-col cols="12" md="6">
            <UploadDocumentsForContract v-model="documents" />
        </v-col>
    </v-row>
</template>

<script setup lang="ts">
    import CountryTable from '~/components/products/information/CountryTable.vue'
    import UploadDocumentsForContract from '~/components/products/UploadDocumentsForContract.vue'

    const contract = defineModel<any>()
    const documents = ref([])

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        if (contract.value?.id) await loadData()
        if (contract.value?.insuranceLocations?.length) {
            contract.value?.insuranceLocations.sort((a, b) => a.countryName.localeCompare(b.countryName))
        }
        if (contract.value?.citizenships?.length) {
            contract.value?.citizenships.sort((a, b) => a.countryName.localeCompare(b.countryName))
        }
    })

    async function loadData(): Promise<void> {
        try {
            const {images} = await app.$api.ownProductContractFiles.get(supplierStore.fullContract?.id)
            documents.value = images
        } catch (e) {
            documents.value = []
        }
    }

    const uploadFile = async (id?) => {
        const contractId = id ? id : supplierStore.fullContract?.id

        const toDelete = documents.value.filter(el => !!el.toDelete)
        if (toDelete?.length) await deleteFiles(toDelete)

        try {
            const toSaveFiles = documents.value.filter(({id}) => !id)
            await Promise.all(
                toSaveFiles.map(async el => {
                    const formData = new FormData()
                    formData.append('imageFile', el, el.name)
                    return await app.$api.ownProductContractFiles.post(contractId, formData)
                })
            )
            await loadData()
            app.$toast.success(t('success_message.saved_successfully'))
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        }
    }

    const deleteFiles = async toDelete => {
        for (let i = 0; i < toDelete.length; i++) {
            await app.$api.ownProductContractFiles.remove(supplierStore.fullContract?.id, toDelete[i]?.id)
        }
    }

    defineExpose({
        uploadFile,
    })
</script>
